import React, { useEffect, useState } from "react";
import { SocialIcon } from "../utils/social-icon";
import { ItemWithMetaData, Rating } from "../api/Items";
import { atLeastOneItem, classNames, formattedDate } from "../utils";
import Divider from "../shared-components/LineDivider";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import {
  useCreateItemPostRating,
  useRemoveRatingAndKeywordsPostsItem,
} from "../api/ItemsApi";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import {
  BehaviorCheckboxGroup,
  SystemEditMediaModalActions,
} from "../system/SystemEditMediaModal";
import { debounce } from "lodash";
import {
  contentMediaToMediaExternalInternal,
  PostImage,
} from "../posts/PostImage";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { LinkIcon } from "@heroicons/react/24/outline";
import RatingBadges from "../shared-components/RatingBadges";
import KeywordBadges from "../shared-components/KeywordBadges";
import { getDisplayPostTypeText } from "../utils/post-utils";
import { justBehaviorRatings } from "../system/helpers";

interface AmendmentChildrenProps {
  className: string;
  post: ItemWithMetaData;
}

type PostRatingBehavior = {
  name: string;
  score_type: "behavior";
};

const Body = ({ className, post }: AmendmentChildrenProps) => {
  const [applyLineClamp, setApplyLineClamp] = useState<boolean>(true);
  const [showReadMore, setShowReadMore] = useState<boolean>(true);
  const hasImage = post.item.content.media?.length > 0;
  const containerRef = React.useRef(null);

  useEffect(() => {
    const hasClamping = (el: HTMLParagraphElement) => {
      const { clientHeight, scrollHeight } = el;
      return clientHeight !== scrollHeight;
    };

    const checkButtonAvailability = () => {
      if (!containerRef.current) return;

      setShowReadMore(hasClamping(containerRef.current));
    };

    const debouncedCheck = debounce(checkButtonAvailability, 50);

    checkButtonAvailability();
    window.addEventListener("resize", debouncedCheck);

    return () => {
      window.removeEventListener("resize", debouncedCheck);
    };
  }, [containerRef]);

  return (
    <div className={className}>
      {hasImage && (
        <div className="flex items-start pr-2 w-32">
          <PostImage
            media={contentMediaToMediaExternalInternal(post.item.content)}
            noPadding={true}
          />
        </div>
      )}
      <div className="flex-1 font-normal text-base leading-6">
        <p
          ref={containerRef}
          className={classNames(
            `text-gray-500 text-xs font-light`,
            applyLineClamp ? "line-clamp-3" : "line-clamp-6"
          )}
        >
          {post.item.content.text}
        </p>
        {showReadMore && (
          <button
            className="text-cyan-500 font-light text-xs"
            onClick={() => setApplyLineClamp(!applyLineClamp)}
          >
            Read {applyLineClamp ? "More" : "Less"}
          </button>
        )}
      </div>
    </div>
  );
};

const BehaviorFlags = ({ className, post }: AmendmentChildrenProps) => {
  const behaviorRatings = justBehaviorRatings(post.rating);
  if (behaviorRatings.length === 0) return <></>;

  return (
    <div className={className}>
      <span className="text-gray-600">Behavior Flags:</span>
      <div className="py-2">
        <RatingBadges ratings={behaviorRatings} />
      </div>
    </div>
  );
};

const Header = ({ className, post }: AmendmentChildrenProps) => {
  const displayDate = formattedDate(new Date(post.item.content.created_at));
  const postTypeText = getDisplayPostTypeText(post);
  return (
    <div className={className}>
      <SocialIcon
        url={post.item.content.profile_url}
        className="pointer-events-none"
      />
      <div className="flex flex-col ">
        <div className="text-sm font-medium mx-3 text-gray-700">{`${postTypeText} on ${post.source?.name}`}</div>
        <div className="text-sm font-normal mx-3 text-gray-500">
          {displayDate}
        </div>
      </div>
      <div className="flex ml-auto">
        <LinkIcon className="w-5 h-4 text-blue-500" />
        <a
          className="text-gray-500 text-xs ml-1 font-light"
          href={post.item.content.url}
          rel="noreferrer"
          target="_blank"
        >
          View Link
        </a>
      </div>
    </div>
  );
};

function PostButton({
  text,
  onClick,
}: {
  text: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) {
  return (
    <Button
      text={text}
      onClick={onClick}
      className="text-gray-500"
      colorType={ButtonColorTypes.custom}
    />
  );
}

function ErrorList({ list }: { list: string[] }) {
  return (
    <>
      {list.map((text) => (
        <ErrorMessageContainer message={text} />
      ))}
    </>
  );
}

const Buttons = ({
  isReadOnly,
  inEdit,
  handleOnEdit,
  handleSave,
  handleRemove,
}: {
  isReadOnly: boolean;
  inEdit: boolean;
  handleOnEdit: () => void;
  handleSave: () => void;
  handleRemove: () => void;
}) => {
  return (
    <>
      {!isReadOnly && !inEdit && (
        <PostButton text="Edit Flags" onClick={handleOnEdit} />
      )}
      {!isReadOnly && inEdit && <PostButton text="Save" onClick={handleSave} />}
      {!isReadOnly && <PostButton text="Remove" onClick={handleRemove} />}
    </>
  );
};

const AmendmentPost = ({
  post,
  behaviors,
  handleAction,
  isReadOnly,
}: {
  post: ItemWithMetaData;
  behaviors: string[];
  handleAction: (action: "remove" | "edit", post?: ItemWithMetaData) => void;
  isReadOnly?: boolean;
}) => {
  const {
    mutateAsync: createItemRating,
    isLoading: isLoadingItemRating,
    error: errorCreateItemRating,
  } = useCreateItemPostRating();
  const { modal, show, hide } = useConfirmationDialog();

  const {
    data: removeData,
    mutate: removeItem,
    isLoading: loadingRemove,
    error: errorRemove,
  } = useRemoveRatingAndKeywordsPostsItem();
  const [inEdit, setInEdit] = useState<boolean>(false);
  const [ratingSelections, setRatingSelections] = useState<
    PostRatingBehavior[]
  >([]);

  useEffect(() => {
    if (removeData) handleAction("remove");
  }, [removeData, handleAction]);

  function onCheckboxChange(ratings: PostRatingBehavior[]) {
    setRatingSelections(ratings);
  }

  function handleRemove() {
    show(
      "",
      "Are you sure you want to remove the post?",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          removeItem({
            item_uuids: [post.item.external_id],
          });
          hide();
        }
      }
    );
  }

  function handleUpdateRating(
    action: SystemEditMediaModalActions,
    ratings: PostRatingBehavior[]
  ) {
    if (action === "save") {
      createItemRating({
        item_uuid: post.item.external_id,
        ratings,
      }).then(() => {
        handleAction("edit", {
          ...post,
          rating: ratingSelections.map((selections) => ({
            external_id: "",
            name: selections.name,
            rater_uuid: "",
            score_type: selections.score_type,
          })),
        });
      });
    }
  }

  function handleOnEdit() {
    setInEdit(true);
  }
  function handleSave() {
    handleUpdateRating("save", ratingSelections);
    setInEdit(false);
  }

  function getErrorList() {
    const errors = [];

    if (errorRemove) errors.push("Error Removing ");

    if (errorCreateItemRating) errors.push("Error Creating Ratings");

    return errors;
  }

  const errors = getErrorList();

  return (
    <div
      className={classNames(
        "flex flex-col p-4 rounded-lg shadow-md bg-white overflow-hidden",
        isLoadingItemRating ? "opacity-30 pointer-events-none" : ""
      )}
    >
      <Header
        className="flex flex-nowrap items-center mb-6 cursor-pointer"
        post={post}
      />
      <Body className="flex flex-1" post={post} />
      {!inEdit && <BehaviorFlags className="mt-2" post={post} />}
      {post.keywords && atLeastOneItem(post.keywords) && (
        <KeywordBadges keywords={post.keywords} />
      )}
      {inEdit && (
        <InlineBehaviorCheck
          behaviors={behaviors}
          ratings={post.rating}
          onChange={onCheckboxChange}
        />
      )}
      <Divider addMargin={false} className={"mt-3"} />
      <div className="flex justify-between gap-4 mt-1">
        <Buttons
          isReadOnly={!!isReadOnly}
          inEdit={inEdit}
          handleOnEdit={handleOnEdit}
          handleRemove={handleRemove}
          handleSave={handleSave}
        />
      </div>
      {errors.length > 0 && <ErrorList list={errors} />}
      {(loadingRemove || isLoadingItemRating) && <LoadingSpinner />}
      {modal}
    </div>
  );
};

type InlineBehaviorCheckProps = {
  behaviors: string[];
  ratings: Rating[];
  onChange: (ratings: PostRatingBehavior[]) => void;
};

function InlineBehaviorCheck({
  behaviors,
  ratings,
  onChange,
}: InlineBehaviorCheckProps) {
  return (
    <>
      <div className="grid grid-cols-2 mt-2 flex-wrap gap-3 border rounded-md p-2">
        <BehaviorCheckboxGroup
          behaviors={behaviors}
          initialSelection={ratings ?? []}
          onChange={onChange}
        />
      </div>
    </>
  );
}

export default AmendmentPost;
